/* LuzLed instant search: an index of every site product, built live by the store
   (templates/collection.search-index.liquid; assets/luz-search-index.json from tools/luz_search_index.py is the fallback),
   searched in the browser as you type. Used by the search panel that
   opens from the header and by the /search page. */
(() => {
  if (window.LuzSearch) return;

  const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const compact = (s) => norm(s).replace(/[^a-z0-9]/g, "");
  const stem = (w) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w);
  const words = (s) => norm(s).split(/[^a-z0-9.]+/).filter(Boolean).map(stem);
  /* Other words buyers use for the same thing (request 5, research/2026-10-11-search-synonyms/): UK, US and trade
     names, colour words for the colour temperatures, and the common Spanish terms. A query word matches itself or any
     word listed for it; keys and words are written as words() leaves them (lower case, no plural s). */
  const SYN = {
    bulb: ["lamp"], lightbulb: ["lamp"], gls: ["a60", "a65", "a70"], edison: ["st64", "st48"], squirrel: ["st64"],
    golf: ["g45"], candelabra: ["e12", "candle"], ses: ["e14"], bc: ["b22"], gu53: ["gu5.3"], mr16: ["gu5.3"], mr11: ["gu4"],
    spotlight: ["spot"], downlighter: ["downlight"], can: ["downlight"], pot: ["downlight"], potlight: ["downlight"],
    troffer: ["panel"], fluorescent: ["tube", "batten"], tape: ["strip"], ribbon: ["strip"], channel: ["profile"],
    extrusion: ["profile"], psu: ["power", "driver"], transformer: ["power", "driver"], adapter: ["power"],
    ballast: ["driver"], dimmer: ["dimmer", "controller"], motion: ["sensor"], pir: ["sensor"], occupancy: ["sensor"],
    hanging: ["pendant", "suspended"], luminaire: ["light"], fixture: ["light", "fixture"], fitting: ["light"],
    flood: ["floodlight"], highbay: ["bay"], ufo: ["ufo", "bay"], streetlight: ["street"], plafond: ["ceiling"],
    warm: ["warm", "3000k", "2700k", "2200k"], neutral: ["neutral", "4000k"], cool: ["cool", "6500k", "6000k"],
    cold: ["6500k", "6000k"], daylight: ["6500k", "6000k"],
    bombilla: ["lamp"], foco: ["spot", "downlight"], empotrable: ["recessed"], tira: ["strip"], perfil: ["profile"],
    carril: ["track"], magnetico: ["magnetic"], colgante: ["pendant"], plafon: ["ceiling"], proyector: ["floodlight"],
    farola: ["street"], campana: ["bay"], tubo: ["tube"], regleta: ["batten", "linear"], fuente: ["power"],
    alimentacion: ["power"], transformador: ["power", "driver"], regulador: ["dimmer", "controller"], mando: ["remote"],
    sensor: ["sensor"], conector: ["connector"], solar: ["solar"], estanca: ["weatherproof", "batten"],
    aluminum: ["aluminium"], color: ["colour"], gray: ["grey"], meter: ["m"], liter: [],
    // Spanish plurals in -es keep their e after the plural rule
    perfile: ["profile"], carrile: ["track"], proyectore: ["floodlight"], reguladore: ["dimmer", "controller"],
    conectore: ["connector"], transformadore: ["power", "driver"], sensore: ["sensor"] };
  const alts = (t) => SYN[t] || [];
  /* Two word trade terms read as one, before the words are split ("pot light" is a downlight, not any light). */
  const PHRASES = [[/\b(pot|can) lights?\b/g, "downlight"], [/\brecessed lights?\b/g, "recessed"], [/\b(led )?(tape|ribbon)( lights?)?\b/g, "strip"],
    [/\btira (de )?led\b/g, "strip"], [/\bedison (bulbs?|lamps?)\b/g, "edison"], [/\bedison screw\b/g, "e27"],
    [/\bgls (bulbs?|lamps?)\b/g, "gls"], [/\bgolf balls?\b/g, "g45"], [/\bsquirrel cages?\b/g, "st64"],
    [/\bflood lights?\b/g, "floodlight"], [/\bfuentes? de alimentacion\b/g, "power supply"],
    [/\b(warm|cool|cold|neutral|daylight) white\b/g, "$1"], [/\bblanco (calido|frio|neutro)\b/g, (m, c) => ({ calido: "warm", frio: "cool", neutro: "neutral" })[c]]];
  /* Sizes as people type them: 600x600 or 60x60 (panels are named in centimetres in the trade), and 120cm, read as
     the names write them (600 x 600, 1200 mm). */
  const sizes = (t) => t
    .replace(/(\d+(?:\.\d+)?)\s*[x×*]\s*(\d+(?:\.\d+)?)/g, (m, a, b) =>
      [30, 60, 120].includes(+a) && [60, 120].includes(+b) ? `${a * 10} x ${b * 10}` : `${a} x ${b}`)
    .replace(/(\d+(?:\.\d+)?)\s*cm\b/g, (m, a) => `${Math.round(a * 10)} mm`);
  const phrase = (q) => sizes(PHRASES.reduce((t, [re, to]) => t.replace(re, to), norm(q)));
  /* The everyday words for the content pages, and two entries that are not pages of the store. */
  const PAGE_WORDS = { "/pages/how-to-order": "quote quotes order ordering request price prices pricing buy purchase lead time",
    "/pages/warranty": "guarantee claim claims return returns repair defect faulty garantia",
    "/pages/contact": "email sales phone message enquiry inquiry contacto", "/pages/distributors": "wholesale wholesaler dealer reseller partner trade distribuidor",
    "/pages/about": "company brand who", "/pages/cookies": "cookie consent", "/policies/privacy-policy": "privacy data gdpr personal",
    "/cart": "quote list cart basket my list request" };
  const EXTRA_PAGES = [{ t: "Privacy policy", u: "/policies/privacy-policy" }, { t: "Your quote list", u: "/cart" }];
  /* Typing slips: a word found nowhere is read as the closest word of the catalogue (one letter off, two for long words). */
  const near = (a, b, max) => {
    if (Math.abs(a.length - b.length) > max) return false;
    const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
    for (let j = 1; j <= b.length; j++) d[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[a.length][b.length] <= max;
  };

  let index = null;
  let loading = null;
  /* The index is built live by the store (templates/collection.search-index.liquid, request 35): every page of 250
     products loads at once (the layout gives the count; page 1 confirms it). Kept for ten minutes in this visit, so moving between
     pages does not load it again; the static asset is the fallback if the store does not answer. */
  const KEY = "luz-search-index-2", TTL = 10 * 60 * 1000;
  const live = async () => {
    try {
      const c = JSON.parse(sessionStorage.getItem(KEY) || "null");
      if (c && Date.now() - c.t < TTL) return c.d;
    } catch {}
    const page = (n) => fetch(`/collections/all?view=search-index&page=${n}`, { credentials: "same-origin" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); });
    /* All pages at once when the layout says how many (data-luz-index-pages); any page the count missed follows. */
    const known = +document.documentElement.dataset.luzIndexPages || 1;
    const got = await Promise.all(Array.from({ length: known }, (_, i) => page(i + 1)));
    const more = await Promise.all(Array.from({ length: Math.max(0, got[0].n - known) }, (_, i) => page(known + i + 1)));
    const all = [...got, ...more].slice(0, Math.max(got[0].n, 1));
    const d = { p: all.flatMap((x) => x.p), c: all[0].c, g: all[0].g };
    if (!d.p.length) throw new Error("empty index");
    try { sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now(), d })); } catch {}
    return d;
  };
  const load = () => {
    if (index) return Promise.resolve(index);
    if (!loading) {
      loading = live().catch(() => fetch(document.documentElement.dataset.luzIndex || window.LuzSearchIndex).then((r) => r.json()))
        .then((d) => {
          d.p.forEach((e) => {
            const f = e.f || {};
            e._name = new Set(words(e.n));
            // also the filter values: the colour temperatures a selectable product offers, dimmable, base, voltage, shape...
            e._more = new Set([...words(e.s), ...words(e.g), ...words(e.G), ...(f.chips || []).flatMap(words),
              ...(e.o || []).flatMap(words), ...e.k.flatMap((x) => words(x[2])), ...(f.k || []).map((k) => `${k}k`),
              ...(f.dim === true ? ["dimmable"] : []), ...[f.base, f.ip].filter(Boolean).flatMap(words),
              ...["lc", "type", "volt", "ctl", "colour", "shape", "glass", "mount"].flatMap((k) => (f[k] || []).flatMap(words))]);
            e._codes = [[e.c, e.u, "", (e.k.find((k) => k[0] === e.c) || e.k[0] || [])[3]], ...e.k].map((x) => [compact(x[0]), x]);
            e._cat = new Set(words(e.g));
          });
          d.c.forEach((c) => (c._w = new Set(words(`${c.t} ${c.p}`))));
          d.g = [...d.g, ...EXTRA_PAGES.filter((x) => !d.g.some((g) => g.u === x.u))];
          d.g.forEach((g) => (g._w = new Set(words(`${g.t} ${PAGE_WORDS[g.u] || ""}`))));
          d._vocab = new Set([...d.p.flatMap((e) => [...e._name, ...e._more]), ...d.c.flatMap((c) => [...c._w]), ...d.g.flatMap((g) => [...g._w])]);
          index = d;
          return d;
        });
    }
    return loading;
  };

  const hit = (set, t) => {
    if (set.has(t)) return 2;
    for (const w of set) if (w.startsWith(t)) return 1;
    return 0;
  };

  /* Score one product for a query: an exact or leading code match wins; otherwise every word of the
     query must be found (at the start of a word) in the name, series, category, options or values. */
  const score = (e, q, toks, qc) => {
    let codeScore = 0, item = null;
    const codeish = /\d/.test(qc) || qc.startsWith("luz");
    if (qc.length >= 2) {
      for (const [cc, x] of e._codes) {
        // a LuzLed code also matches without its LUZ prefix (GL12, FLS); a spare part whose code is its accounting
        // name matches only in full, so a word like "panel" or a value like "3000k" never pulls it up
        const real = cc.startsWith("luz"), bare = real ? cc.slice(3) : cc;
        const partial = real || !e.f?.part;
        let s = 0;
        if (cc === qc || bare === qc) s = 1000;
        else if (partial && qc.length >= 3 && (codeish || real) && (cc.startsWith(qc) || bare.startsWith(qc))) s = 600 - (bare.length - qc.length);
        else if (partial && codeish && qc.length >= 4 && cc.includes(qc)) s = 300;
        if (s > codeScore) { codeScore = s; item = x; }
      }
    }
    let wordScore = 0, inName = 0;
    if (toks.length) {
      for (const t of toks) {
        const n = Math.max(hit(e._name, t), ...alts(t).map((a) => hit(e._name, a)));
        const m = Math.max(hit(e._more, t), ...alts(t).map((a) => hit(e._more, a)));
        const c = /\d/.test(t) && e._codes.some(([cc]) => cc.includes(t.replace(/[^a-z0-9]/g, ""))) ? 1 : 0;
        if (!n && !m && !c) { wordScore = 0; inName = -1; break; }
        wordScore += n === 2 ? 30 : n ? 20 : m === 2 ? 12 : m ? 8 : 6;
        // a product of the category the word names comes before one that only mentions it (troffer: panels first)
        if (hit(e._cat, t) || alts(t).some((a) => hit(e._cat, a))) wordScore += 5;
        if (n) inName++;
      }
      if (inName === toks.length) wordScore += 15;
      if (wordScore && e.f?.part) wordScore -= 4;  // spare parts after the products themselves, as on the category pages
    }
    const total = Math.max(codeScore, wordScore) + (codeScore && wordScore ? 50 : 0);
    return { s: total, cs: codeScore, item: codeScore ? item : null };
  };

  const query = (q) => {
    const d = index;
    q = phrase(q);
    let toks = words(q);
    const qc = compact(q);
    if (!d || (!toks.length && !qc)) return { p: [], c: [], g: [] };
    let fixed = null;
    const known = (t) => /\d/.test(t) || hit(d._vocab, t) || alts(t).some((a) => hit(d._vocab, a));
    if (toks.some((t) => !known(t) && t.length >= 4) && !d.p.some((e) => e._codes.some(([cc]) => cc.includes(qc)))) {
      toks = toks.map((t) => {
        if (known(t) || t.length < 4) return t;
        const max = t.length >= 8 ? 2 : 1;
        let best = null;
        for (const w of d._vocab) if (w[0] === t[0] && w.length > 2 && near(t, w, max) && (!best || Math.abs(w.length - t.length) < Math.abs(best.length - t.length))) best = w;
        return best || t;
      });
      fixed = toks.join(" ");
    }
    const p = [];
    d.p.forEach((e) => {
      const r = score(e, q, toks, qc);
      if (r.s > 0) p.push({ e, s: r.s, cs: r.cs, item: r.item });
    });
    // a query that leads codes (LUZ-R0...) shows the code matches only
    const codeLike = /\d/.test(qc) && qc.length >= 4;
    // ...and the parts and accessories named after that model (the glass for GL12, the end cap for CO019), after it
    if (codeLike && p.some((x) => x.cs >= 600)) p.splice(0, p.length, ...p.filter((x) => x.cs >= 300 || x.e._name.has(qc)));
    p.sort((a, b) => b.s - a.s || a.e.n.localeCompare(b.e.n, "en", { numeric: true }));
    const on = (set) => toks.length && toks.every((t) => hit(set, t) || alts(t).some((a) => hit(set, a)));
    return { p, c: d.c.filter((c) => on(c._w)).slice(0, 6), g: d.g.filter((g) => on(g._w)).slice(0, 4), toks, fixed };
  };

  const esc = (t) => String(t ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
  const mark = (text, toks) => {
    let out = esc(text);
    (toks || []).filter((t) => t.length > 1).forEach((t) => {
      out = out.replace(new RegExp(`(^|[^a-z0-9])(${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"), "$1<mark>$2</mark>");
    });
    return out;
  };

  window.LuzSearch = { load, query, esc, mark, compact };

  /* ---------- The search panel ---------- */
  const panel = document.querySelector("[data-lsx]");
  if (!panel) return;
  const input = panel.querySelector("[data-lsx-q]");
  const body = panel.querySelector("[data-lsx-body]");
  const status = panel.querySelector("[data-lsx-status]");
  const start = body.innerHTML;
  const ROOT = window.Shopify?.routes?.root || "/";
  const NOIMG = `<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 6v6M9.5 12.5l4 4M38.5 12.5l-4 4M16 30a8 8 0 1 1 16 0c0 3-2 4.5-3 6.5h-10c-1-2-3-3.5-3-6.5ZM19 41h10"/></svg>`;
  const PLUS = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg><svg class="ok" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5 10 17 19 7"/></svg>`;
  if (/Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)) panel.querySelectorAll("[data-lsx-mod]").forEach((k) => (k.textContent = "⌘"));
  /* Products viewed lately (kept in this browser by the product page), shown before anything is typed. */
  const recent = () => {
    let list = [];
    try { list = JSON.parse(localStorage.getItem("luz-recent") || "[]"); } catch {}
    if (!list.length) return "";
    return `<div class="lsx-recent"><h3>Recently viewed</h3><div class="lsx-list" role="listbox" aria-label="Recently viewed">${list.map((r, n) =>
      `<a class="lsx-row" id="lsx-r${n}" role="option" href="${esc(r.u)}"><span class="lsx-img">${r.i ? `<img src="${esc(r.i)}" alt="" loading="lazy">` : NOIMG}</span>` +
      `<span class="lsx-t"><b>${esc(r.n)}</b><small><span class="lsx-code">${esc(r.c || "")}</span></small></span></a>`).join("")}</div></div>`;
  };
  /* Quick add (request 79): a buyer who knows the codes fills the quote list from the search, one of each, then sets the
     quantities in the list. */
  const addItem = (v, btn, label) => {
    if (!v) return;
    /* The quote list limits (theme settings, window.LuzLimits); the line as the index names it (assets/luz-list.js). */
    const { lines, qty } = window.LuzLimits;
    const r = window.LuzList.add({ v, ...(index && window.LuzList.find(index, v)) }, 1, "luz-search");
    if (r.blocked) {
      status.innerHTML = `<span>${esc(window.LuzList.BLOCKED)}</span>`;
      return;
    }
    if (r.full === "list") {
      status.innerHTML = `<span>Your quote list holds up to ${lines} products. Send it, then start a new list for the rest.</span><a href="${ROOT}cart">View quote list</a>`;
      return;
    }
    if (r.full) {
      status.innerHTML = `<span>Your list already holds ${qty.toLocaleString("en")} of <b>${esc(label)}</b>, the most one list takes.</span><a href="${ROOT}cart">View quote list</a>`;
      return;
    }
    btn?.classList.add("done");
    status.innerHTML = `<span><b>${esc(label)}</b> added to your quote list.</span><a href="${ROOT}cart">View quote list (${r.count})</a>`;
  };
  body.addEventListener("click", (e) => {
    const b = e.target.closest("[data-lsx-add]");
    if (b) { e.preventDefault(); addItem(b.dataset.lsxAdd, b, b.dataset.label); }
  });
  let rows = [], active = -1, last = null, openedBy = "key";

  const open = (by = "key") => {
    if (!panel.hidden) return;
    openedBy = by;
    last = document.activeElement;
    panel.hidden = false;
    document.documentElement.classList.add("lsx-lock");
    requestAnimationFrame(() => panel.classList.add("on"));
    input.focus();
    input.select();
    status.innerHTML = "";
    if (!input.value) render();
    load().then(() => input.value && render());
  };
  const close = () => {
    if (panel.hidden) return;
    panel.classList.remove("on");
    document.documentElement.classList.remove("lsx-lock");
    setTimeout(() => (panel.hidden = true), 180);
    /* Focus goes back to where it was; after a search opened with the mouse, without the keyboard focus ring (it shows
       again with the next key press). */
    if (last && last !== document.body && openedBy === "pointer") {
      last.classList.add("no-ring");
      const on = () => { last.classList.remove("no-ring"); removeEventListener("keydown", on, true); removeEventListener("pointerdown", on, true); };
      addEventListener("keydown", on, true);
      addEventListener("pointerdown", on, true);
    }
    last?.focus?.({ preventScroll: true });
  };
  const setActive = (i) => {
    rows.forEach((r, n) => r.classList.toggle("on", n === i));
    active = i;
    if (rows[i]) { rows[i].scrollIntoView({ block: "nearest" }); input.setAttribute("aria-activedescendant", rows[i].id); }
    else input.removeAttribute("aria-activedescendant");
  };

  const render = () => {
    const q = input.value.trim();
    if (!q) { body.innerHTML = recent() + start; rows = [...body.querySelectorAll(".lsx-row")]; active = -1; input.setAttribute("aria-expanded", "false"); return; }
    if (!index) { load().then(render); return; }
    const r = query(q);
    const all = `${window.Shopify?.routes?.root || "/"}search?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
    if (!r.p.length && !r.c.length && !r.g.length) {
      body.innerHTML = `<div class="lsx-none"><p>Nothing found for <b>${esc(q)}</b>.</p><p>Try a shorter part of the code, such as LUZ-R06, or a product type such as downlight or floodlight.</p>${start}</div>`;
      rows = []; active = -1;
      return;
    }
    const prods = r.p.slice(0, 8).map(({ e, item }, n) => {
      const code = item ? item[0] : e.c;
      const label = item && item[2] ? item[2] : "";
      const href = item ? item[1] : e.u;
      const v = item ? item[3] : (e.k[0] || [])[3];
      const name = `${e.n}${label ? `, ${label}` : ""} (${code})`;
      /* The listbox holds the options only: the wrapper has no role and the + (a pointer shortcut; Shift Enter adds from
         the keyboard, as the hint line says) is hidden from screen readers (audit 2026-10-11). */
      return `<div class="lsx-item" role="none">${v ? `<button type="button" class="lsx-add" data-lsx-add="${v}" data-label="${esc(name)}" aria-hidden="true" tabindex="-1">${PLUS}</button>` : ""}<a class="lsx-row" id="lsx-p${n}" role="option" href="${href}"${v ? ` data-v="${v}" data-label="${esc(name)}"` : ""}>
        <span class="lsx-img">${e.i ? `<img src="${esc(e.i)}" alt="" loading="lazy" crossorigin="anonymous">` : `<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 6v6M9.5 12.5l4 4M38.5 12.5l-4 4M16 30a8 8 0 1 1 16 0c0 3-2 4.5-3 6.5h-10c-1-2-3-3.5-3-6.5ZM19 41h10"/></svg>`}</span>
        <span class="lsx-t"><b>${mark(e.n, r.toks)}</b><small><span class="lsx-code">${mark(code, [])}</span>${label ? ` &middot; ${esc(label)}` : ""}${e.v > 1 && !label ? ` &middot; ${e.v} versions` : ""}</small></span>
        <span class="lsx-chips">${(e.f?.chips || []).slice(0, 2).map((c) => `<i>${esc(c)}</i>`).join("")}</span>
      </a></div>`;
    }).join("");
    const side = [
      r.c.length ? `<div class="lsx-side-g"><h3 id="lsx-ch">Categories</h3><div role="listbox" aria-labelledby="lsx-ch">${r.c.map((c, n) => `<a class="lsx-link" id="lsx-c${n}" role="option" href="${c.u}"><span>${mark(c.t, r.toks)}</span>${c.p ? `<small>${esc(c.p)}</small>` : ""}</a>`).join("")}</div></div>` : "",
      r.g.length ? `<div class="lsx-side-g"><h3 id="lsx-gh">Pages</h3><div role="listbox" aria-labelledby="lsx-gh">${r.g.map((g, n) => `<a class="lsx-link" id="lsx-g${n}" role="option" href="${g.u}"><span>${mark(g.t, r.toks)}</span></a>`).join("")}</div></div>` : "",
    ].join("");
    body.innerHTML = `${r.fixed ? `<p class="lsx-fix">Showing results for <b>${esc(r.fixed)}</b></p>` : ""}<div class="lsx-res${side ? "" : " lsx-res--one"}">
        <div class="lsx-main">
          ${r.p.length ? `<h3>Products <span>${r.p.length}</span></h3><div class="lsx-list" role="listbox" aria-label="Products">${prods}</div>` : ""}
          ${r.p.length > 8 ? `<a class="lsx-all" href="${all}">See all ${r.p.length} results<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></a>` : ""}
        </div>
        ${side ? `<aside class="lsx-side">${side}</aside>` : ""}
      </div>`;
    rows = [...body.querySelectorAll(".lsx-row, .lsx-link")];
    input.setAttribute("aria-expanded", "true");
    setActive(-1);
  };

  let t = 0;
  input.addEventListener("input", () => { clearTimeout(t); t = setTimeout(render, 40); });
  /* Arrow keys move by position, as the results are laid out: up and down within a column, left and right between the
     columns (and to the categories and pages beside them). Up from the first row goes back to the typing. */
  const step = (dir) => {
    if (active < 0) { if (dir === "down" && rows.length) setActive(0); return; }
    const a = rows[active].getBoundingClientRect();
    const ax = a.left + a.width / 2, ay = a.top + a.height / 2;
    let best = -1, bestScore = Infinity;
    rows.forEach((r, i) => {
      if (i === active) return;
      const b = r.getBoundingClientRect();
      const bx = b.left + b.width / 2, by = b.top + b.height / 2;
      const vertical = dir === "down" || dir === "up";
      const ahead = dir === "down" ? b.top >= a.bottom - 2 : dir === "up" ? b.bottom <= a.top + 2 : dir === "right" ? b.left >= a.right - 2 : b.right <= a.left + 2;
      if (!ahead) return;
      const main = vertical ? Math.abs(by - ay) : Math.abs(bx - ax), cross = vertical ? Math.abs(bx - ax) : Math.abs(by - ay);
      const score = main + cross * 3;
      if (score < bestScore) { bestScore = score; best = i; }
    });
    if (best >= 0) setActive(best);
    else if (dir === "up") setActive(-1);
  };
  input.addEventListener("keydown", (e) => {
    const dir = { ArrowDown: "down", ArrowUp: "up", ArrowLeft: "left", ArrowRight: "right" }[e.key];
    if (dir && (dir === "down" || dir === "up" || active >= 0)) { e.preventDefault(); step(dir); }
    else if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      const target = rows[active] || rows.find((r) => r.dataset.v);
      if (target?.dataset.v) addItem(target.dataset.v, target.parentElement.querySelector("[data-lsx-add]"), target.dataset.label);
    }
    else if (e.key === "Enter") {
      e.preventDefault();
      const target = rows[active] || rows[0];
      if (active >= 0 && target) location.href = target.href;
      else if (input.value.trim()) panel.querySelector("form").submit();
    }
  });
  panel.addEventListener("click", (e) => { if (e.target.closest("[data-lsx-close]")) close(); });
  document.addEventListener("click", (e) => {
    const b = e.target.closest("[data-luz-search-open]");
    if (b) { e.preventDefault(); open(e.detail === 0 ? "key" : "pointer"); }
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !panel.hidden) { close(); return; }
    const typing = /input|textarea|select/i.test(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
    if (!typing && (e.key === "/" || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k"))) { e.preventDefault(); open("key"); }
  });
  /* The recently viewed list is deleted when the visitor turns preferences off in the cookie banner (cookie policy). */
  document.addEventListener("visitorConsentCollected", (e) => {
    if (e.detail && !e.detail.preferencesAllowed) try { localStorage.removeItem("luz-recent"); } catch {}
  });
})();
